/**
 * Parent block — father / mother names, parent phone/email/aadhar + parent address.
 * Pulls AddressSection internally with the `parent` prefix.
 *
 * Single parent UX:
 *   - "Single parent?" Yes/No select at the top of the block.
 *   - If Yes, the user picks which parent is the active guardian (Father / Mother)
 *     and the OTHER parent's name field is hidden (and the value cleared).
 *   - If No, both parent name fields are shown and `single_parent_type` is cleared.
 */
import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AddressSection } from "./AddressSection";

const onPhoneInput = (set, key) => (e) => set(key, e.target.value.replace(/[^\d+\-\s]/g, ""));

function SingleParentToggle({ form, set, idPrefix }) {
  const isSingle = !!form.is_single_parent;
  const onYesNo = (v) => {
    const yes = v === "yes";
    set("is_single_parent", yes);
    if (yes) {
      // Default to "father" when user first selects Yes (so the UI always has a valid pick).
      if (!form.single_parent_type) set("single_parent_type", "father");
    } else {
      set("single_parent_type", "");
    }
  };
  const onParentPick = (v) => {
    set("single_parent_type", v);
    // Clear the OTHER parent name so we don't keep stale data.
    if (v === "father") set("parent_mother_name", "");
    if (v === "mother") set("parent_father_name", "");
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label>Single parent?</Label>
        <Select value={isSingle ? "yes" : "no"} onValueChange={onYesNo}>
          <SelectTrigger data-testid={`${idPrefix}-single-parent`} className="bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="no">No</SelectItem>
            <SelectItem value="yes">Yes</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {isSingle && (
        <div>
          <Label>Single parent is</Label>
          <Select value={form.single_parent_type || "father"} onValueChange={onParentPick}>
            <SelectTrigger data-testid={`${idPrefix}-single-parent-type`} className="bg-white capitalize"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="father">Father</SelectItem>
              <SelectItem value="mother">Mother</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

export function ParentSection({ form, set, idPrefix = "adm", requireRequired = false }) {
  const isSingle = !!form.is_single_parent;
  const singleType = form.single_parent_type || "";
  const showFather = !isSingle || singleType === "father";
  const showMother = !isSingle || singleType === "mother";
  const mark = requireRequired ? " *" : "";

  return (
    <div className="space-y-4">
      <SingleParentToggle form={form} set={set} idPrefix={idPrefix} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {showFather && (
          <div>
            <Label>Father's name{requireRequired || isSingle ? " *" : ""}</Label>
            <Input data-testid={`${idPrefix}-father`} required={requireRequired || isSingle} value={form.parent_father_name || ""} onChange={(e) => set("parent_father_name", e.target.value)} className="bg-white" />
          </div>
        )}
        {showMother && (
          <div>
            <Label>Mother's name{requireRequired || isSingle ? " *" : ""}</Label>
            <Input data-testid={`${idPrefix}-mother`} required={requireRequired || isSingle} value={form.parent_mother_name || ""} onChange={(e) => set("parent_mother_name", e.target.value)} className="bg-white" />
          </div>
        )}
        <div>
          <Label>Parent phone (numeric){mark}</Label>
          <Input data-testid={`${idPrefix}-parent-phone`} required={requireRequired} inputMode="numeric" pattern="[0-9+\-\s]*" value={form.parent_phone || ""} onChange={onPhoneInput(set, "parent_phone")} className="bg-white font-mono" />
        </div>
        <div>
          <Label>Parent email <span className="text-[10px] text-[var(--vs-text-secondary)] font-normal">(optional)</span></Label>
          <Input data-testid={`${idPrefix}-parent-email`} type="email" value={form.parent_email || ""} onChange={(e) => set("parent_email", e.target.value)} className="bg-white" />
        </div>
      </div>
      <AddressSection form={form} set={set} prefix="parent" testIdPrefix={idPrefix} requireRequired={requireRequired} />
      <div>
        <Label>Parent Aadhar (encrypted)</Label>
        <Input data-testid={`${idPrefix}-parent-aadhar`} value={form.parent_aadhar || ""} onChange={(e) => set("parent_aadhar", e.target.value)} className="bg-white font-mono" placeholder="XXXX-XXXX-XXXX" />
      </div>
    </div>
  );
}
